/**
 * Wave eeeee · CommandBar — global Ctrl+K / Cmd+K search palette.
 *
 * Mounted once at the app root via AppShell. Opens on:
 *   - Ctrl+K / Cmd+K (anywhere)
 *   - "/" keypress while focus is NOT inside an input/textarea/select/[contenteditable]
 *
 * Keyboard navigation:
 *   ArrowDown / ArrowUp — move selection
 *   Enter               — open selected result
 *   Escape              — close
 *
 * Empty state: shows recently visited surfaces from localStorage.
 * Debounce: 200ms before firing the network call.
 *
 * One intentional modal: the command palette is the "right tool" for
 * the job. Modal overlay + backdrop-blur keeps focus locked here while
 * the PM jumps between contexts.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useT } from '@/hooks/useT';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { cn } from '@/lib/cn';
import {
  type GlobalHit,
  type GlobalResultKind,
  flattenResults,
  getRecentEntries,
  hitHref,
  pushRecentEntry,
  type RecentEntry,
} from '@/lib/global-search';

// ─── constants ────────────────────────────────────────────────────────────────

const KIND_ORDER: GlobalResultKind[] = [
  'project',
  'estimate',
  'budgetItem',
  'po',
  'supplier',
  'rdo',
  'fvs',
  'drawing',
  'rfi',
];

const DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 2;

// ─── component ────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CommandBar({ open, onClose }: Props) {
  const { t } = useT();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Trap Tab focus inside the palette and restore focus to whatever was
  // focused (the search button / page) when it closes.
  useFocusTrap(panelRef, open);

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [hits, setHits] = useState<GlobalHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [recent, setRecent] = useState<RecentEntry[]>([]);

  // Debounce the server call.
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, open]);

  // Fire search when debounced term changes.
  useEffect(() => {
    if (!open) return;
    if (debounced.length < MIN_QUERY_LENGTH) {
      setHits([]);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    api
      .globalSearch(debounced, 20)
      .then((data) => {
        if (!active) return;
        setHits(flattenResults(data));
        setCursor(0);
      })
      .catch(() => {
        if (!active) return;
        setHits([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [debounced, open]);

  // Reset on close (after exit animation).
  useEffect(() => {
    if (!open) {
      const t = window.setTimeout(() => {
        setQuery('');
        setDebounced('');
        setHits([]);
        setCursor(0);
      }, 160);
      return () => window.clearTimeout(t);
    }
    // Load recent on open.
    setRecent(getRecentEntries());
    // Focus input after scale-in starts.
    const t = window.setTimeout(() => inputRef.current?.focus(), 20);
    return () => window.clearTimeout(t);
  }, [open]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Group hits by kind in stable display order.
  const grouped = useMemo(() => {
    const map = new Map<GlobalResultKind, GlobalHit[]>();
    for (const h of hits) {
      const arr = map.get(h.kind) ?? [];
      arr.push(h);
      map.set(h.kind, arr);
    }
    const flat: Array<
      | { type: 'header'; kind: GlobalResultKind }
      | { type: 'hit'; hit: GlobalHit; index: number }
    > = [];
    let idx = 0;
    for (const kind of KIND_ORDER) {
      const rows = map.get(kind);
      if (!rows || rows.length === 0) continue;
      flat.push({ type: 'header', kind });
      for (const hit of rows) {
        flat.push({ type: 'hit', hit, index: idx++ });
      }
    }
    return flat;
  }, [hits]);

  const activate = useCallback(
    (hit: GlobalHit) => {
      pushRecentEntry(hit);
      onClose();
      navigate(hitHref(hit));
    },
    [navigate, onClose],
  );

  const activateRecent = useCallback(
    (entry: RecentEntry) => {
      onClose();
      navigate(entry.href);
    },
    [navigate, onClose],
  );

  const selectableCount = hits.length;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (selectableCount === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => (c + 1) % selectableCount);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (c - 1 + selectableCount) % selectableCount);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = hits[cursor];
      if (hit) activate(hit);
    }
  };

  // Scroll selected row into view.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cursor="${cursor}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  const showRecent = debounced.length < MIN_QUERY_LENGTH && recent.length > 0;
  const showEmpty = !loading && debounced.length >= MIN_QUERY_LENGTH && hits.length === 0;

  if (!open) return null;

  return createPortal(
    <div
      aria-hidden={!open}
      className="fixed inset-0 z-50"
      onKeyDown={onKeyDown}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label={t.common.close}
        onClick={onClose}
        className="absolute inset-0 bg-ink/40 dark:bg-black/60 backdrop-blur-sm animate-fade-in"
      />

      {/* Palette card */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t.wave12.search.label}
        className={cn(
          'fixed left-1/2 top-[12vh] w-[min(92vw,640px)] -translate-x-1/2',
          'rounded-2xl border border-line bg-surface shadow-pop dark:shadow-pop-dark',
          'overflow-hidden animate-scale-in',
        )}
        style={{ transformOrigin: 'center top' }}
      >
        {/* Search input row */}
        <div className="flex items-center gap-2 border-b border-line px-4 py-3">
          <SearchIcon />
          <input
            ref={inputRef}
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder={t.wave12.search.placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            // The input owns the listbox and points at the active option so
            // screen readers announce the highlighted row as the user arrows
            // through results. We keep the implicit textbox role (no explicit
            // role="combobox") so existing getByRole('textbox') queries hold.
            aria-controls="cmdbar-listbox"
            aria-activedescendant={
              hits.length > 0 ? `cmdbar-option-${cursor}` : undefined
            }
            className={cn(
              'flex-1 bg-transparent text-[15px] text-ink',
              'placeholder:text-ink-subtle focus:outline-none',
            )}
          />
          <kbd className="hidden sm:inline-flex h-6 items-center rounded border border-line px-1.5 text-[11px] font-medium text-ink-muted bg-surface-2">
            Esc
          </kbd>
        </div>

        {/* Results area */}
        <div ref={listRef} id="cmdbar-listbox" className="max-h-[60vh] overflow-y-auto py-1" role="listbox">
          {/* Loading spinner */}
          {loading && hits.length === 0 && (
            <div className="px-4 py-6 text-sm text-ink-muted">{t.wave12.search.loading}</div>
          )}

          {/* No results */}
          {showEmpty && (
            <div className="px-4 py-6 text-sm text-ink-muted">{t.wave12.search.noResults}</div>
          )}

          {/* Prompt to type more */}
          {!loading && debounced.length < MIN_QUERY_LENGTH && recent.length === 0 && (
            <div className="px-4 py-6 text-sm text-ink-muted">{t.wave12.search.placeholder}</div>
          )}

          {/* Recently visited (empty state when no query) */}
          {showRecent && (
            <>
              <div className="mt-2 px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-ink-subtle">
                {t.wave12.search.recent}
              </div>
              {recent.map((entry) => (
                <button
                  key={`recent-${entry.kind}-${entry.id}`}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => activateRecent(entry)}
                  className={cn(
                    'pressable flex w-full items-start gap-3 px-4 py-2.5 text-left',
                    'hover:bg-surface-2 transition-colors duration-100',
                  )}
                >
                  <KindIcon kind={entry.kind} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-ink">{entry.title}</div>
                    {entry.subtitle && (
                      <div className="truncate text-xs text-ink-muted">{entry.subtitle}</div>
                    )}
                  </div>
                </button>
              ))}
            </>
          )}

          {/* Search results grouped by kind */}
          {grouped.map((row) =>
            row.type === 'header' ? (
              <div
                key={`h-${row.kind}`}
                className="mt-2 px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-ink-subtle"
              >
                {kindLabel(row.kind, t)}
              </div>
            ) : (
              <button
                key={`${row.hit.kind}-${row.hit.id}`}
                id={`cmdbar-option-${row.index}`}
                type="button"
                role="option"
                aria-selected={cursor === row.index}
                data-cursor={row.index}
                onMouseEnter={() => setCursor(row.index)}
                onClick={() => activate(row.hit)}
                className={cn(
                  'pressable flex w-full items-start gap-3 px-4 py-2.5 text-left',
                  'transition-colors duration-100',
                  cursor === row.index ? 'bg-surface-3' : 'hover:bg-surface-2',
                )}
              >
                <KindIcon kind={row.hit.kind} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-ink">{row.hit.title}</div>
                  {row.hit.subtitle && (
                    <div className="truncate text-xs text-ink-muted">{row.hit.subtitle}</div>
                  )}
                </div>
              </button>
            ),
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── label helpers ────────────────────────────────────────────────────────────

function kindLabel(kind: GlobalResultKind, t: ReturnType<typeof useT>['t']): string {
  const g = t.commandBar.groups;
  switch (kind) {
    case 'project':    return g.projects;
    case 'estimate':   return g.estimates;
    case 'budgetItem': return g.budgetItems;
    case 'po':         return g.pos;
    case 'supplier':   return g.suppliers;
    case 'rdo':        return g.rdos;
    case 'fvs':        return g.fvs;
    case 'drawing':    return g.drawings;
    case 'rfi':        return g.rfis;
  }
}

// ─── icons ────────────────────────────────────────────────────────────────────

function SearchIcon() {
  return (
    <svg className="h-4 w-4 text-ink-muted shrink-0" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
      <path d="M14 14l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function KindIcon({ kind }: { kind: GlobalResultKind }) {
  const cls = 'mt-0.5 h-4 w-4 text-ink-muted shrink-0';
  switch (kind) {
    case 'project':
      return (
        <svg className={cls} viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M3 17V9l7-5 7 5v8H3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
      );
    case 'estimate':
      return (
        <svg className={cls} viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <rect x="4" y="3" width="12" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
          <path d="M7 7h6M7 10h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M11 14l1.5-1.5L14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'budgetItem':
      return (
        <svg className={cls} viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <rect x="3" y="5" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
          <path d="M10 8v4M8 10h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case 'po':
      return (
        <svg className={cls} viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M3 6h14l-1.5 9h-11L3 6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M7 6V4h6v2" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      );
    case 'supplier':
      return (
        <svg className={cls} viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <circle cx="10" cy="7" r="3" stroke="currentColor" strokeWidth="1.6" />
          <path d="M4 17c1.2-3 3.4-4.5 6-4.5s4.8 1.5 6 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case 'rdo':
      return (
        <svg className={cls} viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <rect x="4" y="3" width="12" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
          <path d="M7 7h6M7 10h6M7 13h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case 'fvs':
      return (
        <svg className={cls} viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M5 10l4 4 6-7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
    case 'drawing':
      return (
        <svg className={cls} viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <rect x="3" y="3" width="14" height="14" rx="1" stroke="currentColor" strokeWidth="1.6" />
          <path d="M7 7h6M7 10h3M7 13h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <path d="M13 13l2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      );
    case 'rfi':
      return (
        <svg className={cls} viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.6" />
          <path d="M10 9v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <circle cx="10" cy="6.5" r="0.8" fill="currentColor" />
        </svg>
      );
  }
}
